import type { LoaderFunctionArgs } from '@remix-run/node';
import { useEffect, useRef } from 'react';
import { json, redirect } from '@remix-run/node';
import { useLoaderData, useLocation, useSearchParams } from '@remix-run/react';
import { mergeMeta } from '~/modules/seo';
import type { AvatarProps } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import {
    useAppDisplayModeContext,
    Container,
    Box,
} from '@archipro-design/aria';
import { getDirectoryPageMeta } from '@modules/category/api';
import { NoResultsSection } from '@modules/category/component/no-results/NoResultsSection';
import { CategoryFilterSection } from '@modules/category/component/category-filter-section/CategoryFilterSection';
import {
    querySearchServiceAggregate,
    querySearchServiceSearch,
} from '~/modules/search';
import { mapESProfessionalTileViewModel } from '@modules/search/util/elasticsearch-professional-tile-view';
import { createESAggregationProps } from '@modules/search/util/create-es-aggregation-props';
import {
    createEmptyFilterStates,
    createFilterStates,
} from '~/modules/search/util/create-filter-states';
import { getProfessionalsCategories } from '@modules/professionals-category/api';
import AdvertBannerTile from '@modules/adverts/component/advert-banner-tile/AdvertBannerTile';
import { createPaginationState } from '@modules/pagination/util/create-pagination-state.server';
import { TitleSectionDesktop } from '~/modules/category/component/title-section/TitleSection.desktop';
import { TitleSectionMobile } from '~/modules/category/component/title-section/TitleSection.mobile';
import {
    getPageLocalisation,
    getProfessionalsLocation,
    getProfessionalCategoriesInLocation,
} from '~/modules/seo/api/get-page-localisation.server';
import { logDirectoryItemClick } from '~/modules/category/util/log-directory-item-click';
import { useTracker } from '@archipro-website/tracker';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '~/modules/search/hook/use-search-filters';
import type { SearchLocation } from '~/modules/search/type/api';
import AppliedFilters from '@modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '@modules/search/util/reset-filter-states';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import { getDirectoryFullName } from '~/modules/category/util/directory-helpers';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import type { ProfessionalTileViewModel } from '~/modules/professional/type';
import { getSession } from '~/modules/root/util/session.server';
import TitleSectionFilterMobile from '~/modules/category/component/title-section/TitleSectionFilter.mobile';
import { shuffleArray } from '~/utils/arrayHelper';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { checkTeamMemberInvitation } from '~/modules/professionals-category/util/check-team-member-invitation.server';
import { adRequest, adsRequest } from '@modules/adverts/api';
import { advertCreativeProfessionalTileView } from '@modules/professionals-category/util/advert-creative-professional-tile-view';
import ProfessionalAdvertTile from '@modules/tile/component/professional-tile/ProfessionalAdvertTile';
import { AdvertProjectPlacementTile } from '~/modules/adverts/component/advert-project-placement/AdvertProjectPlacementTile';
import type { ProjectAd } from '~/modules/adverts/type';
import { getProjectPlacements } from '~/modules/adverts/utils/project-placement-helper.server';
import { parseURLToESQueryParams } from '~/modules/search/util/filter-url-helpers';
import { parseURL } from '@archipro-website/top-navigation';
import {
    hasFeature,
    useFeature,
    useTileHotButtons,
    type APHandle,
} from '~/modules/root';
import { useUser } from '~/modules/user';
import APConnectBanner from '~/modules/category/component/ap-connect-banner/APConnectBanner';
import * as S from '~/modules/professionals-category/components/professional-category-container/ProfessionalCategoryContainer.style';
import getShowProfessionalArchiproConnectBanner from '~/utils/get-show-professional-archipro-connect-banner';
import { getAppliedFilterNumber } from '~/modules/search/util/filter-state-util';
import type { ProfessionalTileViewModel as ProfessionalTileViewModelRocco } from '@rocco/ui/tile-professional/view-models/professional';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { getProfessionalsFromESSearchResults } from '~/modules-rocco/search/data-mappers/tile-professional';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { getActiveDirectoryCategoriesCount } from '~/modules-rocco/categories/api/get-active-directory-categories-count.server';
import { mapCategoryCountToPlaceholder } from '~/modules-rocco/categories/data-mappers/category-count-to-placeholder';
import { mapProfessionalToTileViewModel } from '~/modules-rocco/professionals/data-mappers/tile-professional';
import { getRecentlyViewedServer } from '~/modules-rocco/recently-viewed/api/get-recently-viewed.server';
import { CategoryPageRecentlyViewed } from '~/ui-rocco/pages/category-page/CategoryPageRecentlyViewed';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { buildPaginationUrl } from '~/modules-rocco/pagination/util/build-pagination-url';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { CategoryPageFAQ } from '~/ui-rocco/pages/category-page/CategoryPageFAQ';
import { CollapsibleToggle } from '~/modules/category/component/collapsible-page-layout/CollapsibleToggle';
import { response404 } from '@modules/root/graphql/assertResponse.server';
import { B2BLandingPage } from '@modules/b2b/B2BLandingPage';
import { profsConfig } from '@modules/b2b/config/profs.config';
import {
    collectPartnerUrlSegments,
    getB2BPartnerBanners,
} from '@modules/b2b/api/get-b2b-partner-banners.server';
import {
    buildB2BRootLoaderData,
    isB2BRootLoaderData,
} from '~/utils/business-subdomain';
import { getHost, isBusinessHost } from '~/utils/request.server';
import CategoryPageLocations from '@ui-rocco/pages/category-page/CategoryPageLocations';
import CategoryPageCategoriesInLocation from '@ui-rocco/pages/category-page/CategoryPageCategoriesInLocation';
import { buildLocationMeshData } from '~/modules/professionals-category/util/location-breadcrumb';
import { buildProfessionalDirectoryDisplayTitle } from '~/modules/professionals-category/util/location-meta';
import { buildLocationPathRedirect } from '~/modules/professionals-category/util/location-redirect';
import { buildProfessionalsMetaDescriptors } from '~/modules/professionals-category/util/professionals-meta';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { FollowSelectorProxy } from '~/modules/moodboards/components';
import { getProfessionalCategories as getProfessionalCategoriesRocco } from '~/modules-rocco/professionals/api/get-professional-categories.server';
import { parseURL as parseURLRocco } from '@modules-rocco/url/api/parse-url';
import { mapCategoriesToTileViewModel } from '@modules-rocco/categories/data-mappers/category-to-tile';
import ProfessionalCategoryExploreSection from '@ui-rocco/pages/category-page/ProfessionalCategoryExploreSection';
import { buildContextualProfessionalCategories } from '~/modules/professionals-category/util/build-contextual-professional-categories';
import { parseFooterContentToFAQ } from '~/modules/professionals-category/util/parse-footer-content-to-faq.server';

export const meta = mergeMeta<typeof loader>(
    // This function overrides meta exported from root
    () => [],
    buildProfessionalsMetaDescriptors,
);

export const handle: APHandle = {
    isCategoryPage: true,
    hideContactArchiPro: false,
    // Big-A unified header (BC-4480) — matches the approved directory mock.
    header: { showLogomark: true },
};

const DEFAULT_PROFESSIONAL_PAGE_SIZE = 36;
const DEFAULT_SORT = 'trending';
// insert project placement ads every 2 rows.
const ADS_SLOT_GAP_MOBILE = 2; // 1 tiles per row for mobile
const MAX_ADS_SLOT = 3;

const MAX_SPONSORED_AD_TILES_DISPLAYED = 3;

/** Consumer professionals directory data fetch (apex host). */
const loadConsumerProfessionals = async (args: LoaderFunctionArgs) => {
    const { request, context } = args;

    const redirectOpts = await checkTeamMemberInvitation(args);
    if (redirectOpts) {
        return redirect(redirectOpts.url, redirectOpts.init);
    }

    const { appDisplayMode, user, featureFlags } = context;
    const url = new URL(request.url);
    const isMobile = appDisplayMode === 'mobile';
    const { basePath, params } = parseURL(url.pathname);

    // We do not show product suppliers in the professionals directory page.
    if (basePath === '/professionals/product-supplier') {
        return redirect('/products/suppliers');
    }

    // Check location for urls /location-*
    let locationParams = {};
    let forceFilters = {};
    // Resolved location for category × location pages, kept here because the
    // `location-{slug}` path segment only yields a slug; we use the GraphQL-resolved
    // location to drive location-aware tiles and structured data. Set for both path
    // `location-{slug}` pages and `?location=` query filters.
    let appliedLocation: SearchLocation | undefined;
    // Slug from a path `location-{slug}` segment only. `parseURL` also fills
    // `params.location` from a `?location=` query filter, so we derive this from the
    // pathname to keep the geographic breadcrumb, hub treatment, mesh blocks,
    // location-aware titles and thin-page noindex limited to true location pages.
    const hasPathLocation = url.pathname
        .split('/')
        .some((segment) => segment.startsWith('location-'));
    let locationSlug: string | undefined;
    if (params.location || url.searchParams.get('location')) {
        const locationString = (params.location?.toString() ??
            url.searchParams.get('location')) as string;
        locationSlug = hasPathLocation
            ? params.location?.toString()
            : undefined;
        const location = await getProfessionalsLocation(args, locationString);
        if (!location) {
            throw response404();
        }

        // Location filters canonically live in the path (`/location-{slug}`), where the
        // page can be indexed and sitemapped. Permanently redirect the legacy
        // `?location={slug}` query form so each page has exactly one URL.
        if (!hasPathLocation) {
            return redirect(
                buildLocationPathRedirect(
                    basePath,
                    location.Slug ?? locationString,
                    url.searchParams,
                ),
                301,
            );
        }

        locationParams = {
            suburb: location.Suburb,
            district: location.District,
            city: location.City,
            region: location.Region,
            state: location.State,
            country: location.Country,
        };
        appliedLocation = {
            suburb: location.Suburb ?? undefined,
            district: location.District ?? undefined,
            city: location.City ?? undefined,
            region: location.Region ?? undefined,
            state: location.State ?? undefined,
            country: location.Country ?? undefined,
        };
        forceFilters = {
            Location: {
                Suburb: location.Suburb,
                District: location.District,
                City: location.City,
                Region: location.Region,
                State: location.State,
                Country: location.Country,
                // Lets the backend IsIndexableFilterPage verdict match the combo
                // registry to exactly one place. Ignored by content localisation.
                Slug: location.Slug ?? locationString,
            },
        };
    }

    const session = await getSession(request);
    const searchParams = url.searchParams;

    const queryES = async (pageSize: number) => {
        const { queryParams } = parseURLToESQueryParams(
            `${url.pathname}?${searchParams.toString()}`,
            'professional',
            'directory',
            {
                pageSize,
                // url parameter
                ...locationParams,
            },
            DEFAULT_SORT,
            // Exclude product suppliers from the professional directory page.
            { excludeProductSuppliers: true },
        );
        const aggregationProps = createESAggregationProps({
            ...queryParams,
            searchParams,
        });
        const searchPromise = querySearchServiceSearch({
            context,
            queryParams,
        });
        const aggregationPromise = querySearchServiceAggregate({
            context,
            queryParams: aggregationProps,
        });

        return Promise.all([
            queryParams,
            aggregationProps,
            searchPromise,
            aggregationPromise,
        ]);
    };

    const sponsoredAdsPromise = adRequest({
        type: 'SponsorshipDirectoryPlacement',
        requestUrl: request.url,
        addPathContext: ['region'],
        ...args.context,
    });

    const [
        directoryPageMeta,
        // rootDirectoryPageMeta will be used as a fallback for missing category descriptions
        rootDirectoryPageMeta,
        professionalCategory,
        recentlyViewedProfessionalsResult,
        ads,
        localisation,
        sponsoredAds,
        esResults,
        activeDirectoryCategoriesCount,
        rootProfessionalCategoriesResult,
        categoriesInLocation,
    ] = await Promise.all([
        getDirectoryPageMeta({
            url,
            ...context,
            forcedFilters: forceFilters,
        }),
        // fetch the root professionals category meta to use its description as a fallback
        getDirectoryPageMeta({
            url: new URL('/professionals', request.url),
            ...context,
        }),
        getProfessionalsCategories({ args, ...args.context }),
        getRecentlyViewedServer({ type: 'Professionals' }, args.context),
        adsRequest({
            type: 'DirectoryWithProjectPlacementGroup',
            requestUrl: request.url,
            addPathContext: ['region'],
            ...args.context,
        }),
        getPageLocalisation(args, {
            filters: forceFilters,
        }),
        sponsoredAdsPromise,
        // Waterfall call to query Sponsored Ads and professionals.
        // We need to wait for Sponsored Ads to be able to determine how many professionals we should query for the current page.
        (async () => {
            const sponsoredAdsResult = await sponsoredAdsPromise;
            return queryES(
                sponsoredAdsResult && sponsoredAdsResult.length > 0
                    ? DEFAULT_PROFESSIONAL_PAGE_SIZE -
                          Math.min(
                              sponsoredAdsResult.length,
                              MAX_SPONSORED_AD_TILES_DISPLAYED,
                          )
                    : DEFAULT_PROFESSIONAL_PAGE_SIZE,
            );
        })(),
        getActiveDirectoryCategoriesCount({}, args.context),
        // Root category tree for contextual bottom-of-page internal links (SSR on all viewports).
        getProfessionalCategoriesRocco(
            {
                urlInfo: parseURLRocco('/professionals'),
                excludeNonServiceCategories: true,
            },
            args.context,
        ),
        // Only path-based `location-{slug}` pages get the categories-in-place mesh
        // block (hub: browse categories here; category × location: sibling categories).
        locationSlug
            ? getProfessionalCategoriesInLocation(
                  { context, request },
                  locationSlug,
              )
            : Promise.resolve([]),
    ]);

    const [queryParams, aggregationProps, searchResult, aggregations] =
        esResults;

    const pagination = createPaginationState({
        request,
        totalCount: searchResult?.nbHits ?? 0,
        maxDisplayCount: searchResult?.maxResultWindow,
        defaultPageSize: DEFAULT_PROFESSIONAL_PAGE_SIZE,
    });

    const adsShuffled = ads?.CategoryPlacement
        ? shuffleArray(ads.CategoryPlacement)
        : [];

    const sponsoredAdsShuffled = sponsoredAds
        ? shuffleArray(sponsoredAds).slice(0, MAX_SPONSORED_AD_TILES_DISPLAYED)
        : [];

    // Calculate filter count early so we can determine if tiles should be eager-loaded
    const filterStates = aggregations
        ? createFilterStates({
              aggregations,
              appliedFilters: queryParams.filters,
              aggregationsParams: aggregationProps.aggregations,
              searchType: 'professional',
              visibilityType: 'directory',
              categoryLink: basePath,
              rootText: 'All',
              sortBy: queryParams.sortBy ?? null,
              defaultSortBy: DEFAULT_SORT,
          })
        : createEmptyFilterStates({
              visibilityType: 'directory',
              defaultSortBy: DEFAULT_SORT,
          });

    // We want to show ads even though the location filter is applied.
    const appliedFilterCount = getAppliedFilterNumber(filterStates, [
        'searchLocation',
    ]);

    // Determine if preloading is enabled (no ads/banners at top of grid)
    // We use the same condition for setting eager loading on tiles
    const shouldPreloadImages =
        appliedFilterCount > 0 ||
        (!adsShuffled.length && !sponsoredAdsShuffled.length);
    const numberTilesToEagerLoad = !isMobile ? 3 : 1;

    const professionals = getProfessionalsFromESSearchResults(
        searchResult,
        appliedLocation,
    );
    const tiles: TileViewModel<ProfessionalTileViewModelRocco>[] =
        professionals.map((professional, index) => ({
            aspectRatio: 'default',
            model: professional,
            // Set eager loading for first N tiles to match preloading
            eager: shouldPreloadImages && index < numberTilesToEagerLoad,
        }));

    const professionalTiles =
        searchResult?.hits?.reduce((tiles, hit) => {
            if (hit.PType === 'Professional')
                tiles.push(
                    mapESProfessionalTileViewModel(
                        hit,
                        'medium',
                        isMobile,
                        user,
                    ),
                );
            return tiles;
        }, [] as ProfessionalTileViewModel[]) ?? [];

    const sponsoredAdsTiles = sponsoredAdsShuffled.flatMap((advert) => {
        const professionalTile = advertCreativeProfessionalTileView(
            advert,
            'medium',
        );
        return professionalTile !== null ? professionalTile : [];
    });

    const projectAdsTiles = ads ? getProjectPlacements(ads, isMobile) : [];

    const enableAwardBadges = hasFeature(featureFlags, 'award_badges');
    const searchIslandPlaceholders = mapCategoryCountToPlaceholder(
        activeDirectoryCategoriesCount?.professionals ?? [],
        `Search professionals`,
        'professionals',
    );

    // Augment the loader data with the location-mesh fields (location breadcrumb,
    // hub flag, place name, and sibling categories with the self-link removed).
    const {
        directoryPageMeta: directoryPageMetaWithLocation,
        locationHub,
        appliedLocationName,
        categoriesInLocation: siblingCategoriesInLocation,
        categoryLocations,
    } = buildLocationMeshData({
        directoryPageMeta,
        appliedLocation,
        basePath,
        locationSlug,
        currentPath: url.pathname,
        categoriesInLocation,
        categoryLocations: professionalCategory.locations ?? [],
    });

    const rootCategories = mapCategoriesToTileViewModel(
        rootProfessionalCategoriesResult?.Categories?.edges ?? [],
    );
    const { categories: exploreCategories, title: exploreCategoriesTitle } =
        buildContextualProfessionalCategories({
            rootCategories,
            basePath,
            isRoot: !!directoryPageMetaWithLocation.isRoot,
        });
    const parsedFooterContent = parseFooterContentToFAQ(
        directoryPageMetaWithLocation.footerContent,
    );

    return json({
        userPreferenceSession: session.getUserPreferences(),
        appDisplayMode,
        directoryPageMeta: directoryPageMetaWithLocation,
        locationHub,
        // True only on path-based `location-{slug}` pages; gates location-aware
        // titles and thin-page noindex so `?location=` query filters are excluded.
        isLocationPage: !!locationSlug,
        categoriesInLocation: siblingCategoriesInLocation,
        categoryLocations,
        appliedLocationName,
        searchResult,
        appliedLocation,
        professionalCategory,
        professionalTiles,
        professionalTilesRocco: tiles,
        pagination,
        recentlyViewedItems:
            recentlyViewedProfessionalsResult?.RecentlyViewedProfessionals.map(
                (professional) =>
                    mapProfessionalToTileViewModel(professional, {
                        appDisplayMode,
                    }),
            ) ?? [],
        // If directory doesn't have a description, use the root category description as a fallback
        categoryDescription:
            directoryPageMeta?.directoryHeaderDescription ||
            rootDirectoryPageMeta?.directoryHeaderDescription ||
            "ArchiPro features New Zealand's best professionals and contractors so you can find the help you need for your next building, renovation or redecoration project. We've selected the most trusted and reputable professionals offering quality products and the highest levels of service. Browse their work, discover their services and enquire directly from ArchiPro.",
        filterStates,
        ads: adsShuffled,
        sponsoredAds: sponsoredAdsTiles,
        projectAds: projectAdsTiles,
        localisation,
        url: url.pathname + url.search,
        appliedFilterCount,
        enableAwardBadges,
        searchIslandPlaceholders,
        rootCategories,
        exploreCategories,
        exploreCategoriesTitle,
        parsedFooterContent,
    });
};

export const loader = async (args: LoaderFunctionArgs) => {
    // Business subdomain serves the B2B "Become a Pro" page at /professionals.
    // Branch before any consumer directory data fetching. Sub-paths
    // (e.g. /professionals/architects) have no B2B equivalent, so 404. The B2B
    // payload is cast to the consumer data type so this route's `typeof loader`
    // stays consumer-shaped for the directory component / SEO schema that read
    // it; the B2B branch is recovered at runtime via the `b2b` marker.
    if (isBusinessHost(args.request)) {
        if (args.params['*']) {
            throw response404();
        }
        const partnerBanners = await getB2BPartnerBanners(
            args.context,
            collectPartnerUrlSegments(profsConfig),
        );
        return json({
            ...buildB2BRootLoaderData(getHost(args.request)),
            partnerBanners,
        }) as unknown as Awaited<ReturnType<typeof loadConsumerProfessionals>>;
    }

    return loadConsumerProfessionals(args);
};

const ProfessionalCategoryPage = () => {
    const user = useUser();
    const { setCategory } = useCurrentCategory();
    const { css } = useStyles();
    const {
        professionalCategory,
        categoryDescription,
        directoryPageMeta,
        professionalTiles,
        recentlyViewedItems,
        filterStates,
        ads,
        sponsoredAds,
        projectAds,
        appliedFilterCount,
        professionalTilesRocco,
        pagination,
        exploreCategories,
        exploreCategoriesTitle,
        parsedFooterContent,
        locationHub,
        categoriesInLocation,
        categoryLocations,
        appliedLocationName,
        isLocationPage,
    } = useLoaderData<typeof loader>();

    const appDisplayMode = useAppDisplayModeContext();
    const { desktop, mobile } = appDisplayMode;
    const tracker = useTracker();
    const [searchParams] = useSearchParams();
    // Keep the first value returned by loader to avoid changing when loader revalidate
    const adsMemoized = useLocationMemo(ads, ['region']);
    const sponsoredAdsMemoized = useLocationMemo(sponsoredAds, ['region']);
    const projectAdsMemoized = useLocationMemo(projectAds, ['region']);
    const professionalTilesRoccoAdmin = useTilesAdminData(
        professionalTilesRocco as TileViewModel<ProfessionalTileViewModelRocco>[],
    );
    const { isInDesignBoard } = useHandleFollow();

    // init category tiles tracking
    const gridRef = useRef<HTMLDivElement | null>(null);
    useCategoryTilesTracking(gridRef);

    const remixLocation = useLocation();
    const selectedCategory = getDirectoryFullName(directoryPageMeta);
    const selectedProfessions = searchParams
        .getAll('professionals')
        .join(' | ');
    const selectedRegion = searchParams.getAll('region').join(' | ');

    useEffect(() => {
        tracker.log('professionalListImpression', {
            label: 'Dentsu',
            url: new URL(window.location.href),
            data: {
                category: selectedCategory,
                professionals: selectedProfessions,
                region: selectedRegion,
            },
        });
    }, [selectedCategory, selectedProfessions, selectedRegion, tracker]);

    useEffect(() => {
        setCategory('Professionals');
        return () => setCategory(null);
    }, [setCategory]);

    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();
    const gridColumns = desktop ? (filterDrawerOpen ? 3 : 4) : 1;

    const pageTitle = buildProfessionalDirectoryDisplayTitle({
        directoryHeaderTitle: directoryPageMeta.directoryHeaderTitle,
        categoryTitle: professionalCategory.title,
        appliedLocationName,
        isLocationPage,
        locationHub,
    });

    const professionalIDs = professionalTiles.map(
        (professional) => professional.professionalID,
    );
    const sponsoredAdsIDs = sponsoredAdsMemoized.map(
        (professional) => professional.professionalID,
    );
    const { getTileTopActions, hotButtonModal, getTileAdminCaption } =
        useTileHotButtons({
            itemType: 'professional',
            itemIDs: [...professionalIDs, ...sponsoredAdsIDs],
        });

    const tiles = professionalTilesRoccoAdmin.map((tile) => (
        <CategoryPageTileWithAdminActions key={tile.model.id} tile={tile} />
    ));

    const searchFilters = useSearchFilters({
        category: 'professional',
        initialStates: filterStates,
        isCategoryPage: true,
    });

    if (!appliedFilterCount && sponsoredAdsMemoized.length > 0) {
        sponsoredAdsMemoized.forEach((professionalTile, index) => {
            if (!professionalTile.advertCreative) {
                return;
            }

            const tile = professionalTile.tile;
            const link = professionalTile.advertCreative.cta.href;
            const professionalID = professionalTile.professionalID;

            const imageProps = tile.image.map((image) => {
                return {
                    src: image,
                    width: '100%',
                    height: '100%',
                    sizes: getImagesSizes(595),
                    alt: tile.title,
                };
            });

            const topActions = getTileTopActions({ item: tile });

            const adminCaption = getTileAdminCaption(professionalID);

            const {
                canEdit: _canEdit,
                bmEditorLink: _bmEditorLink,
                ...rest
            } = tile;

            tiles.unshift(
                <FollowSelectorProxy
                    key={`sponsored_${link}`}
                    pinId={professionalID}
                    pinType="ProfessionalPin"
                >
                    {({ openFollowSelector }) => (
                        <ProfessionalAdvertTile
                            tag={'Sponsored'}
                            advertCreative={professionalTile.advertCreative!}
                            prefetch="intent"
                            {...rest}
                            image={imageProps}
                            impression={{
                                professionalID: professionalID,
                                itemID: professionalID,
                                type: 'LibraryItem_Directory',
                            }}
                            logo={{
                                image: {
                                    src:
                                        typeof tile.logo === 'object'
                                            ? ((
                                                  tile.logo as unknown as AvatarProps
                                              ).image as string)
                                            : '',
                                    alt: `${tile.title} professional logo`,
                                    loading:
                                        index < gridColumns ? 'eager' : 'lazy',
                                },
                                variables: (tile.logo as AvatarProps).variables,
                            }}
                            size={
                                !desktop
                                    ? 'small'
                                    : desktop && !filterDrawerOpen
                                      ? 'large'
                                      : 'medium'
                            }
                            variables={{
                                ...(!desktop && {
                                    dynamicSizingWidth: 414,
                                    dynamicSizingGapWidth: 0,
                                    tilePadding: 0,
                                    avatarMargin: 18,
                                }),
                            }}
                            variant={'04'}
                            onFavourite={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                openFollowSelector(e);
                            }}
                            isFavourited={isInDesignBoard(
                                professionalID,
                                'ProfessionalPin',
                            )}
                            services={tile.services.slice(0, 3)}
                            onClick={() => {
                                logDirectoryItemClick(tracker, {
                                    itemID: professionalID,
                                    offset: index,
                                    categoryType: 'professionals',
                                    appDisplayMode,
                                });
                            }}
                            topActions={topActions}
                            adminCaption={adminCaption}
                        />
                    )}
                </FollowSelectorProxy>,
            );
        });
    }

    // Insert a project ad every two rows. at most 3 project ads.
    if (projectAdsMemoized.length) {
        const adsSlotGap = mobile ? ADS_SLOT_GAP_MOBILE : gridColumns * 2;
        const slotCount = Math.min(MAX_ADS_SLOT, tiles.length / adsSlotGap);
        const adsCount = Math.min(MAX_ADS_SLOT, projectAdsMemoized.length);
        let slotIndex = 0;
        let adIndex = 0;
        while (slotIndex < slotCount && adIndex < adsCount) {
            const ad = projectAdsMemoized[adIndex];
            if (!ad) {
                break;
            }
            tiles.splice(
                (slotIndex + 1) * adsSlotGap + slotIndex,
                0,
                getProjectPlacementAdsTile(ad, gridColumns),
            );
            slotIndex++;
            adIndex++;
        }
    }

    if (appliedFilterCount === 0 && adsMemoized.length && adsMemoized[0]) {
        tiles.unshift(
            <AdvertBannerTile
                advertCreative={adsMemoized[0]}
                columns={gridColumns}
                desktop={desktop}
                key="AdvertBannerTile"
            />,
        );
    }

    // oxlint-disable-next-line react-hooks/rules-of-hooks
    const apConnectBannerFlag = useFeature('archipro_connect_category_banner');

    const showProfessionalArchiproBanner =
        getShowProfessionalArchiproConnectBanner(user);

    if (showProfessionalArchiproBanner && apConnectBannerFlag) {
        tiles.unshift(
            <div
                className={css(S.APConnectBannerCell)}
                key="APConnectBannerCell"
            >
                <APConnectBanner />
            </div>,
        );
    }

    return (
        <Box>
            <Container>
                {desktop ? (
                    <TitleSectionDesktop
                        category={pageTitle}
                        rootCategory="Professionals."
                        categoryDescription={categoryDescription}
                        itemsInCategory={filterStates.totalCount}
                        breadcrumbs={directoryPageMeta.breadcrumbs}
                        isRoot={directoryPageMeta.isRoot}
                    />
                ) : (
                    <TitleSectionMobile
                        title={pageTitle}
                        categoryDescription={categoryDescription}
                        subtitle={''}
                    >
                        <TitleSectionFilterMobile
                            esCategory="professional"
                            searchFilters={searchFilters}
                        />
                    </TitleSectionMobile>
                )}

                {desktop && (
                    <CategoryFilterSection
                        category="professional"
                        searchFilters={searchFilters}
                        filterDrawerOpen={filterDrawerOpen}
                        breadcrumbs={directoryPageMeta.breadcrumbs}
                        componentBefore={
                            <CollapsibleToggle
                                filterDrawerOpen={filterDrawerOpen}
                                toggleFilterDrawer={toggleFilterDrawer}
                            />
                        }
                    />
                )}
                <CollapsiblePageLayout
                    sidePanel={
                        desktop ? (
                            <CategoryFilterNav
                                category="professional"
                                searchFilters={searchFilters}
                                hideCategorySectionTitle={true}
                                hideCategoryBackButton={false}
                            />
                        ) : null
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <div>
                        {desktop &&
                            searchFilters.appliedFilters &&
                            searchFilters.appliedFilters.length > 0 && (
                                <AppliedFilters
                                    filterItems={searchFilters.appliedFilters}
                                    resetFilters={() =>
                                        searchFilters.resetFilters(
                                            resetFilterStates(
                                                searchFilters.filterStates,
                                                ['category'],
                                            ),
                                        )
                                    }
                                />
                            )}

                        {tiles.length ? (
                            <Grid
                                ref={gridRef}
                                colClasses={
                                    filterDrawerOpen
                                        ? 'grid-cols-1 md:grid-cols-3 qhd:grid-cols-4'
                                        : 'grid-cols-1 md:grid-cols-4'
                                }
                                rowsGapClass="gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20"
                            >
                                {tiles}
                            </Grid>
                        ) : (
                            <NoResultsSection resultsType={'professionals'} />
                        )}
                        {pagination.totalPages > 1 && (
                            <div className="pt-[6.5625rem] md:pt-20">
                                <TilePagination
                                    currentPath={remixLocation}
                                    current={pagination.page}
                                    total={pagination.totalPages}
                                    urlBuilder={buildPaginationUrl}
                                    visibleCount={
                                        appDisplayMode.desktop ? 4 : 3
                                    }
                                />
                            </div>
                        )}
                    </div>
                </CollapsiblePageLayout>

                <div className="mb-10 mt-20 flex flex-col gap-10 md:mb-20 md:mt-[210px] md:gap-17.5">
                    <div className="flex flex-col">
                        <CategoryPageFAQ
                            faqSchemas={directoryPageMeta.faqSchemas}
                            footerContent={directoryPageMeta.footerContent}
                            parsedFooterContent={parsedFooterContent}
                            variant={desktop ? 'grid' : undefined}
                            seoHeading={
                                isLocationPage && appliedLocationName
                                    ? `Learn about ${professionalCategory.title} in ${appliedLocationName}`
                                    : directoryPageMeta.isRoot
                                      ? undefined
                                      : `Learn about ${directoryPageMeta.directoryHeaderTitle}`
                            }
                        />

                        <CategoryPageLocations
                            category={professionalCategory.title}
                            locations={categoryLocations}
                            isHub={locationHub}
                            continuesFromPreviousSection
                        />

                        <CategoryPageCategoriesInLocation
                            locationName={appliedLocationName}
                            categories={categoriesInLocation}
                            stackedAfterMeshSection
                        />

                        <ProfessionalCategoryExploreSection
                            categories={exploreCategories}
                            title={exploreCategoriesTitle}
                            isRoot={!!directoryPageMeta.isRoot}
                        />
                    </div>

                    <CategoryPageRecentlyViewed
                        displayMode={
                            appDisplayMode.desktop ? 'desktop' : 'mobile'
                        }
                        items={recentlyViewedItems}
                    />
                </div>

                {hotButtonModal}
            </Container>
        </Box>
    );
};

const getProjectPlacementAdsTile = (ad: ProjectAd, gridColumns: number) => {
    return (
        <AdvertProjectPlacementTile
            key={`project-placement-${ad.id}`}
            ad={ad}
            columns={gridColumns}
        />
    );
};

const ProfessionalsRoute = () => {
    const data = useLoaderData<typeof loader>();
    if (isB2BRootLoaderData(data)) {
        return (
            <B2BLandingPage
                config={profsConfig}
                mainSiteOrigin={data.mainSiteOrigin}
                partnerBanners={data.partnerBanners}
            />
        );
    }
    return <ProfessionalCategoryPage />;
};

export default ProfessionalsRoute;
